/**
 * Activity Feed Utilities — Shared between ActivityTimeline (mobile) and NotificationCenter (desktop)
 *
 * Canonical event type system: events are identified by type, not display text.
 * Display labels are derived from canonical types at render time.
 * Dedupe uses canonical type + timestamp, never simplified display text.
 */

import { Package, CheckCircle2, Truck, AlertTriangle, CreditCard, Pencil, XCircle } from "lucide-react";
import type { ComponentType } from "react";

// ─── Canonical Event Types ───────────────────────────────────────────────────
// These are the "meaning" of an event. Parsing, deduplication, and indicator
// selection all key off this enum — never off display strings.

export type CanonicalEventType =
  | "new_order"
  | "awaiting_payment"
  | "ready"
  | "out_for_delivery"
  | "delivered"
  | "delivery_failed"
  | "cancelled"
  | "edited"
  | "payment_received"
  | "assigned"
  | "unassigned"
  | "acknowledged"
  | "comment"
  | "unknown";

/**
 * Classify a raw log message into a canonical event type.
 * Handles both English and Georgian log messages.
 */
export function classifyEvent(message: string): CanonicalEventType {
  const lower = message.toLowerCase();

  // Status changes — match "Status changed to X" or "სტატუსი შეიცვალა: X"
  const statusEn = message.match(/status changed to\s+(.+)/i);
  const statusKa = message.match(/სტატუსი შეიცვალა:\s*(.+)/i);
  const statusTarget = (statusEn?.[1] || statusKa?.[1] || "").trim().toLowerCase();

  if (statusTarget) {
    if (statusTarget.includes("delivered") || statusTarget.includes("მიტანილია")) return "delivered";
    if (statusTarget.includes("out for delivery") || statusTarget.includes("გზაშია")) return "out_for_delivery";
    if (statusTarget.includes("ready") || statusTarget.includes("completed") || statusTarget.includes("მზადაა")) return "ready";
    if (statusTarget.includes("cancelled") || statusTarget.includes("გაუქმებულია")) return "cancelled";
    if (statusTarget.includes("awaiting payment") || statusTarget.includes("გადახდის მოლოდინში")) return "awaiting_payment";
    if (statusTarget.includes("delivery failed") || statusTarget.includes("ვერ მიიტანა")) return "delivery_failed";
    if (statusTarget.includes("new") || statusTarget.includes("ახალი")) return "new_order";
  }

  // Direct matches (non-status-change log lines)
  if (lower.includes("order received") || lower.includes("შეკვეთა მიღებულია") || lower.includes("გარე შეკვეთა მიღებულია")) return "new_order";
  if (lower.includes("awaiting payment") || lower.includes("გადახდის მოლოდინში")) return "awaiting_payment";
  if (lower.includes("delivered") || lower.includes("მიტანილია") || lower.includes("ჩაბარდა")) return "delivered";
  if (lower.includes("out for delivery") || lower.includes("გზაშია")) return "out_for_delivery";
  if (lower.includes("delivery failed") || lower.includes("ვერ მიიტანა")) return "delivery_failed";
  if (lower.includes("cancelled") || lower.includes("გაუქმებულია")) return "cancelled";
  if (lower.includes("order details updated") || lower.includes("შეკვეთის დეტალები განახლდა") || lower.includes("შეკვეთა განახლდა")) return "edited";
  if (lower.includes("payment received") || lower.includes("გადახდა მიღებულია")) return "payment_received";
  if (lower.includes("assigned to session") || lower.includes("სესიაში დამატებულია") || lower.includes("მიენიჭა")) return "assigned";
  if (lower.includes("removed from session") || lower.includes("წაიშალა")) return "unassigned";
  if (lower.includes("acknowledged") || lower.includes("ნანახია") || lower.includes("დადასტურებულია") || lower.includes("პირველად ნანახი")) return "acknowledged";
  if (lower.includes("can't take") || lower.includes("ვერ ვიღებ")) return "delivery_failed";
  if (lower.includes("ready") || lower.includes("მზადაა")) return "ready";

  return "unknown";
}

// ─── Display Labels ──────────────────────────────────────────────────────────

const DISPLAY_LABELS: Record<CanonicalEventType, string> = {
  new_order: "New order",
  awaiting_payment: "Awaiting payment",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  delivery_failed: "Delivery failed",
  cancelled: "Cancelled",
  edited: "Edited",
  payment_received: "Payment received",
  assigned: "Assigned",
  unassigned: "Unassigned",
  acknowledged: "Viewed",
  comment: "Note",
  unknown: "Activity",
};

const DISPLAY_LABELS_KA: Record<CanonicalEventType, string> = {
  new_order: "ახალი შეკვეთა",
  awaiting_payment: "გადახდის მოლოდინში",
  ready: "მზადაა",
  out_for_delivery: "გზაშია",
  delivered: "მიტანილია",
  delivery_failed: "ვერ მიიტანა",
  cancelled: "გაუქმებულია",
  edited: "რედაქტირებულია",
  payment_received: "გადახდა მიღებულია",
  assigned: "მინიჭებულია",
  unassigned: "მოხსნილია",
  acknowledged: "ნანახია",
  comment: "შენიშვნა",
  unknown: "აქტივობა",
};

/**
 * Get a concise display label for an event.
 * Accepts optional language for localization (defaults to English).
 * For "unknown" types, returns a truncated version of the raw message.
 */
export function getDisplayLabel(canonicalType: CanonicalEventType, rawMessage?: string, language?: string): string {
  if (canonicalType === "unknown" && rawMessage) {
    return rawMessage.length > 40 ? rawMessage.slice(0, 40) + "…" : rawMessage;
  }
  const labels = language === "ka" ? DISPLAY_LABELS_KA : DISPLAY_LABELS;
  return labels[canonicalType];
}

// ─── Visual Indicators ───────────────────────────────────────────────────────

interface EventIndicator {
  icon: ComponentType<{ className?: string }>;
  colorClass: string;
}

const INDICATOR_MAP: Record<CanonicalEventType, EventIndicator> = {
  new_order: { icon: Package, colorClass: "text-primary" },
  awaiting_payment: { icon: CreditCard, colorClass: "text-amber-500 dark:text-amber-400" },
  ready: { icon: CheckCircle2, colorClass: "text-primary" },
  out_for_delivery: { icon: Truck, colorClass: "text-primary" },
  delivered: { icon: CheckCircle2, colorClass: "text-emerald-500 dark:text-emerald-400" },
  delivery_failed: { icon: AlertTriangle, colorClass: "text-destructive" },
  cancelled: { icon: XCircle, colorClass: "text-muted-foreground" },
  edited: { icon: Pencil, colorClass: "text-muted-foreground" },
  payment_received: { icon: CreditCard, colorClass: "text-emerald-500 dark:text-emerald-400" },
  assigned: { icon: Truck, colorClass: "text-muted-foreground/70" },
  unassigned: { icon: Truck, colorClass: "text-muted-foreground/40" },
  acknowledged: { icon: Package, colorClass: "text-muted-foreground/40" },
  comment: { icon: Pencil, colorClass: "text-muted-foreground/70" },
  unknown: { icon: Package, colorClass: "text-muted-foreground/70" },
};

export function getEventIndicator(canonicalType: CanonicalEventType): EventIndicator {
  return INDICATOR_MAP[canonicalType];
}

// ─── Noise Detection ─────────────────────────────────────────────────────────
// Events that should be suppressed from the activity feed entirely.

export function isNoiseEvent(canonicalType: CanonicalEventType): boolean {
  return canonicalType === "acknowledged";
}

// ─── Timestamp Parsing ───────────────────────────────────────────────────────
// Extracts timestamp from canonical log line format:
//   [role] · MMM d, yyyy · HH:mm · message
// Also handles Georgian month abbreviations.

const EN_MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const KA_MONTHS: Record<string, number> = {
  "იან": 0, "თებ": 1, "მარ": 2, "აპრ": 3, "მაი": 4, "ივნ": 5,
  "ივლ": 6, "აგვ": 7, "სექ": 8, "ოქტ": 9, "ნოე": 10, "დეკ": 11,
};

/**
 * Parse a timestamp from a log line.
 * Returns null on failure — callers should fall back to order.updated_at.
 *
 * Handles:
 *   [system] · Mar 16, 2026 · 14:30 · message
 *   [system] · მარ. 16, 2026 · 14:30 · message
 */
export function parseTimestampFromLogLine(line: string): Date | null {
  // Pattern: · <month> <day>, <year> · <HH>:<MM> ·
  // Both "Mar" and "მარ." forms, flexible whitespace
  const match = line.match(/·\s*([A-Za-zა-ჰ]+\.?)\s+(\d{1,2}),?\s+(\d{4})\s*·\s*(\d{1,2}):(\d{2})\s*·/);
  if (!match) return null;

  const [, monthStr, dayStr, yearStr, hourStr, minuteStr] = match;
  const monthLower = monthStr.replace(/\.$/, "").toLowerCase();

  let month = EN_MONTHS[monthLower.slice(0, 3)];
  if (month === undefined) {
    // Try Georgian
    month = KA_MONTHS[monthLower];
  }
  if (month === undefined) return null;

  const year = parseInt(yearStr, 10);
  const day = parseInt(dayStr, 10);
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (isNaN(year) || isNaN(day) || isNaN(hour) || isNaN(minute)) return null;

  // Construct as Tbilisi time (UTC+4)
  // We build as UTC then offset by -4h to represent Tbilisi
  const utcMs = Date.UTC(year, month, day, hour - 4, minute);
  return new Date(utcMs);
}

// ─── Dedupe Key ──────────────────────────────────────────────────────────────
// Deterministic identity for an event: orderId + canonical type + timestamp.
// Used to prevent duplicate events when initial load and realtime overlap.

export function buildEventDedupeKey(
  orderId: string,
  canonicalType: CanonicalEventType,
  timestamp: Date,
): string {
  // Round timestamp to the minute to handle minor drift
  const minuteTs = Math.floor(timestamp.getTime() / 60000);
  return `${orderId}:${canonicalType}:${minuteTs}`;
}
